// @tennis-matches/services/trainerstunden/trainer-ui-permissions
// TrainingsDesk: UI-Berechtigungen für Trainer-Logins (ui_permissions JSON am Trainer-Datensatz)

import {
  coordinationTodayYmd,
  isTrainerCoordinatedAtDate,
} from "./trainer-coordination.js";
import { isSubscriptionBillingTab } from "./subscription-plans.js";

/** @typedef {'hub'|'planning'|'agenda'|'abrechnung'|'abrechnen'|'rechnungen'|'rechnungswesen'|'finanzen'|'zahlungen'|'ausgabenerfassung'|'bilanzierung'|'analytics'|'masterdata'|'participants'|'myTrainers'} CoachDeskTabKey */

const BASE_TRAINER_TABS = ["hub", "planning", "analytics"];
const INDEPENDENT_TRAINER_TABS = Object.freeze([
  "hub",
  "planning",
  "abrechnung",
  "finanzen",
  "zahlungen",
  "ausgabenerfassung",
  "bilanzierung",
  "analytics",
  "participants",
  "masterdata",
]);
const INDEPENDENT_TRAINER_MASTERDATA_SUBTABS = Object.freeze([
  "planningFields",
  "invoiceLayout",
  "settings",
  "lists",
  "vereinsabo",
]);
const ELEVATED_TENANT_ROLES = new Set([
  "trainer_coordinator",
  "jugendwart",
  "vereinsadmin",
  "vorstand",
  "admin",
  "owner",
  "kassenwart",
  "sportwart",
]);

/** Rollen, die das Vereinsabo verwalten dürfen. */
export const ELEVATED_TENANT_ROLES_FOR_SUBSCRIPTION = ELEVATED_TENANT_ROLES;

const MASTERDATA_SUBTAB_ORDER = [
  "people",
  "trainers",
  "planningFields",
  "invoiceLayout",
  "dataExport",
  "settings",
  "lists",
  "billing",
  "vereinsabo",
];

function __asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : null;
}

/**
 * @param {unknown} trainerRow
 */
export function resolveTrainerUiPermissions(trainerRow) {
  const perms = __asObject(trainerRow?.ui_permissions) || {};
  const tabsRaw = __asObject(perms.tabs) || {};
  const hasExplicitTabs = Object.keys(tabsRaw).length > 0;

  const rechnungswesen =
    tabsRaw.rechnungswesen === true ||
    tabsRaw.report === true ||
    tabsRaw.settlementTrainer === true;
  const zahlungen =
    tabsRaw.zahlungen === true ||
    tabsRaw.payments === true ||
    (tabsRaw.report === true && tabsRaw.rechnungswesen !== false);
  const ausgabenerfassung =
    tabsRaw.ausgabenerfassung === true || tabsRaw.expenses === true;
  const bilanzierung =
    tabsRaw.bilanzierung === true || tabsRaw.balance === true;

  /** Basis-Reiter: immer aktiv, wenn kein explizites Deny gesetzt wurde */
  const hub = hasExplicitTabs ? tabsRaw.hub !== false : true;
  const planning = hasExplicitTabs ? tabsRaw.planning !== false : true;
  const agenda = hasExplicitTabs ? tabsRaw.agenda !== false : true;

  return {
    tabs: {
      hub,
      planning,
      agenda,
      rechnungswesen,
      zahlungen,
      ausgabenerfassung,
      bilanzierung,
      masterdata: tabsRaw.masterdata === true,
    },
    raw: perms,
    hasExplicitTabs,
  };
}

/**
 * Reiner Trainer-Login ohne Koordinator-/Vereins-Rollen.
 * Solo-Toolkit (alle Tabs) gilt nur, solange der Trainer **nicht** aktiv koordiniert wird.
 * Unter aktiver Koordination greifen die Zugang-Flags (`ui_permissions`).
 *
 * @param {{ mode?: string, trainerRow?: object|null, tenantRoles?: string[] }} input
 */
export function isIndependentTrainerLogin({
  mode,
  trainerRow,
  tenantRoles = [],
} = {}) {
  if (String(mode || "").trim() !== "trainer") return false;
  const roles = (Array.isArray(tenantRoles) ? tenantRoles : [])
    .map((r) =>
      String(r || "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);
  if (!roles.includes("trainer")) return false;
  const loginRole = String(trainerRow?.login_role || "trainer")
    .trim()
    .toLowerCase();
  if (loginRole === "planner") return false;
  for (const role of roles) {
    if (ELEVATED_TENANT_ROLES.has(role)) return false;
  }
  return true;
}

/**
 * Trainer steht unter aktiver Koordination → Zugang-Flags statt Solo-Toolkit.
 * Gleiche inklusive Periodensemantik wie Billing (valid_from ≤ asOf ≤ valid_to).
 * @param {object|null|undefined} trainerRow
 * @param {string} [asOfYmd]
 */
export function isTrainerUnderActiveCoordination(trainerRow, asOfYmd) {
  const asOf = String(asOfYmd || "").trim() || coordinationTodayYmd();
  return isTrainerCoordinatedAtDate(trainerRow, asOf);
}

/**
 * Solo-Toolkit: unabhängiger Trainer-Login und derzeit nicht koordiniert.
 */
function __useIndependentTrainerToolkit({ mode, trainerRow, tenantRoles }) {
  if (!isIndependentTrainerLogin({ mode, trainerRow, tenantRoles })) {
    return false;
  }
  return !isTrainerUnderActiveCoordination(trainerRow);
}

function __tenantRolesAllowSubscriptionManage(tenantRoles = []) {
  const roles = (Array.isArray(tenantRoles) ? tenantRoles : [])
    .map((r) =>
      String(r || "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);
  for (const role of roles) {
    if (ELEVATED_TENANT_ROLES_FOR_SUBSCRIPTION.has(role)) return true;
  }
  return false;
}

/**
 * @param {{ mode?: string, trainerRow?: object|null, hasSelfManagedBilling?: boolean, tenantRoles?: string[] }} input
 * @returns {Set<CoachDeskTabKey>}
 */
export function deriveCoachDeskAllowedTabs({
  mode,
  trainerRow,
  hasSelfManagedBilling = false,
  tenantRoles = [],
} = {}) {
  const viewerMode = String(mode || "coordinator");
  if (viewerMode === "coordinator" || viewerMode === "planner") {
    return new Set([
      "hub",
      "planning",
      "participants",
      "myTrainers",
      "abrechnung",
      "finanzen",
      "zahlungen",
      "ausgabenerfassung",
      "bilanzierung",
      "analytics",
      "masterdata",
    ]);
  }
  if (viewerMode === "team_captain") {
    return new Set(["hub", "planning", "participants"]);
  }

  if (
    __useIndependentTrainerToolkit({
      mode: viewerMode,
      trainerRow,
      tenantRoles,
    })
  ) {
    return new Set(INDEPENDENT_TRAINER_TABS);
  }

  const perms = resolveTrainerUiPermissions(trainerRow);
  const implicitSelfBilling = hasSelfManagedBilling && !perms.hasExplicitTabs;
  const showBilling = perms.tabs.rechnungswesen || implicitSelfBilling;
  const showPayments = perms.tabs.zahlungen || implicitSelfBilling;
  /** @type {CoachDeskTabKey[]} */
  const allowed = [];
  if (perms.tabs.hub) allowed.push("hub");
  if (perms.tabs.planning || perms.tabs.agenda) allowed.push("planning");
  if (perms.tabs.hub || perms.tabs.planning || perms.tabs.agenda) {
    allowed.push("analytics");
  }
  if (showBilling) {
    allowed.push("abrechnung");
  }
  if (showPayments || perms.tabs.ausgabenerfassung || perms.tabs.bilanzierung) {
    allowed.push("finanzen");
  }
  if (showPayments) allowed.push("zahlungen");
  if (perms.tabs.ausgabenerfassung) allowed.push("ausgabenerfassung");
  if (perms.tabs.bilanzierung) allowed.push("bilanzierung");
  if (
    perms.tabs.masterdata ||
    __tenantRolesAllowSubscriptionManage(tenantRoles)
  ) {
    allowed.push("masterdata");
  }
  if (
    deriveCoachDeskParticipantsTabAllowed({
      mode: viewerMode,
      trainerRow,
      tenantRoles,
    })
  ) {
    allowed.push("participants");
  }

  if (!allowed.length) {
    return new Set(BASE_TRAINER_TABS);
  }
  return new Set(allowed);
}

/**
 * Darf Trainer-Stammdaten (Reiter „Trainer“, Anlegen/Bearbeiten) sehen?
 * Nur Koordinator/Planner — nicht reiner Trainer-Login mit Verwaltungsrecht.
 *
 * @param {{ mode?: string }} [input]
 */
export function canManageTrainersMasterdata({ mode } = {}) {
  const viewerMode = String(mode || "coordinator");
  return viewerMode === "coordinator" || viewerMode === "planner";
}

function __sortMasterdataSubTabs(keys) {
  return [...keys].sort(
    (a, b) =>
      MASTERDATA_SUBTAB_ORDER.indexOf(a) - MASTERDATA_SUBTAB_ORDER.indexOf(b),
  );
}

function __stripPeopleFromMasterdataSubTabs(keys) {
  return (Array.isArray(keys) ? keys : []).filter((k) => k !== "people");
}

/**
 * @param {{ mode?: string, trainerRow?: object|null, tenantRoles?: string[] }} input
 * @returns {boolean}
 */
export function deriveCoachDeskParticipantsTabAllowed({
  mode,
  trainerRow,
  tenantRoles = [],
} = {}) {
  const viewerMode = String(mode || "coordinator");
  if (viewerMode === "coordinator" || viewerMode === "planner") return true;
  if (viewerMode === "team_captain") return true;
  if (
    __useIndependentTrainerToolkit({
      mode: viewerMode,
      trainerRow,
      tenantRoles,
    })
  ) {
    return true;
  }

  const perms = resolveTrainerUiPermissions(trainerRow);
  const subtabs = __asObject(perms.raw?.masterdataSubtabs) || {};
  // Explizit abgeschaltet (Verwaltung-Subtab people=false).
  if (Object.keys(subtabs).length && subtabs.people === false) return false;
  // Kontakte ist SideNav-Einstieg (Menschen), nicht mehr nur Stammdaten-Reiter.
  if (perms.tabs.hub || perms.tabs.planning || perms.tabs.agenda) return true;
  if (!perms.tabs.masterdata) return false;
  return true;
}

function __trainerMasterdataSubTabsWithoutManage(perms, includeTrainers) {
  const subtabs = __asObject(perms.raw?.masterdataSubtabs) || {};
  let allowed;
  if (!Object.keys(subtabs).length) {
    // Defaults — danach dieselben „!== false“-Erweiterungen wie bei gesetzten Subtabs.
    allowed = includeTrainers
      ? ["trainers", "people", "settings", "lists"]
      : ["people", "settings", "lists"];
  } else {
    allowed = MASTERDATA_SUBTAB_ORDER.filter((k) => subtabs[k] === true);
    if (!allowed.length) {
      allowed = includeTrainers ? ["trainers"] : ["people"];
    }
  }
  if (!includeTrainers) {
    allowed = allowed.filter((k) => k !== "trainers");
  }
  if (subtabs.people !== false && !allowed.includes("people")) {
    allowed = [...allowed, "people"];
  }
  if (subtabs.settings !== false && !allowed.includes("settings")) {
    allowed = [...allowed, "settings"];
  }
  if (subtabs.lists !== false && !allowed.includes("lists")) {
    allowed = [...allowed, "lists"];
  }
  if (subtabs.planningFields !== false && !allowed.includes("planningFields")) {
    allowed = [...allowed, "planningFields"];
  }
  return __sortMasterdataSubTabs(allowed);
}

/**
 * @param {{ mode?: string, trainerRow?: object|null, tenantRoles?: string[] }} input
 * @returns {string[]}
 */
export function deriveCoachDeskAllowedMasterdataSubTabs({
  mode,
  trainerRow,
  tenantRoles = [],
} = {}) {
  const viewerMode = String(mode || "coordinator");
  if (viewerMode === "coordinator") {
    return __stripPeopleFromMasterdataSubTabs([
      "trainers",
      "planningFields",
      "invoiceLayout",
      "dataExport",
      "settings",
      "lists",
      "billing",
      "vereinsabo",
    ]);
  }
  if (viewerMode === "planner") {
    return __stripPeopleFromMasterdataSubTabs([
      "planningFields",
      "invoiceLayout",
      "dataExport",
      "settings",
      "lists",
      "billing",
      "vereinsabo",
    ]);
  }
  if (viewerMode === "team_captain") {
    return [];
  }

  if (
    __useIndependentTrainerToolkit({
      mode: viewerMode,
      trainerRow,
      tenantRoles,
    })
  ) {
    return [...INDEPENDENT_TRAINER_MASTERDATA_SUBTABS];
  }

  if (viewerMode === "trainer") {
    const allowedTabs = deriveCoachDeskAllowedTabs({
      mode,
      trainerRow,
      tenantRoles,
    });
    if (!allowedTabs.has("masterdata")) {
      if (__tenantRolesAllowSubscriptionManage(tenantRoles)) {
        return ["vereinsabo"];
      }
      return [];
    }
    const perms = resolveTrainerUiPermissions(trainerRow);
    let tabs = __stripPeopleFromMasterdataSubTabs(
      __trainerMasterdataSubTabsWithoutManage(perms, false).filter(
        (key) => key !== "trainers",
      ),
    );
    if (
      __tenantRolesAllowSubscriptionManage(tenantRoles) &&
      !tabs.includes("vereinsabo")
    ) {
      tabs = __sortMasterdataSubTabs([...tabs, "vereinsabo"]);
    }
    return tabs;
  }

  const allowedTabs = deriveCoachDeskAllowedTabs({
    mode,
    trainerRow,
    tenantRoles,
  });
  const perms = resolveTrainerUiPermissions(trainerRow);
  let withOwnTrainer = __stripPeopleFromMasterdataSubTabs(
    __trainerMasterdataSubTabsWithoutManage(perms, true),
  );

  if (!allowedTabs.has("masterdata")) {
    if (__tenantRolesAllowSubscriptionManage(tenantRoles)) {
      return ["vereinsabo"];
    }
    return ["trainers"];
  }

  if (
    __tenantRolesAllowSubscriptionManage(tenantRoles) &&
    !withOwnTrainer.includes("vereinsabo")
  ) {
    withOwnTrainer = __sortMasterdataSubTabs([...withOwnTrainer, "vereinsabo"]);
  }

  if (!withOwnTrainer.includes("trainers")) {
    return __sortMasterdataSubTabs(["trainers", ...withOwnTrainer]);
  }
  return withOwnTrainer;
}

/**
 * Reiner Trainer-Login: eigenes Profil in Verwaltung → Trainer (nur eigene Zeile).
 *
 * @param {{ mode?: string, trainerId?: string }} [input]
 */
export function canTrainerEditOwnMasterdataProfile({ mode, trainerId } = {}) {
  return (
    String(mode || "").trim() === "trainer" &&
    String(trainerId || "").trim() !== ""
  );
}

/**
 * @param {{ mode?: string, trainerRow?: object|null, hasSelfManagedBilling?: boolean, tenantRoles?: string[] }} input
 */
export function deriveCoachDeskHubSurface({
  mode,
  trainerRow,
  hasSelfManagedBilling = false,
  tenantRoles = [],
} = {}) {
  const viewerMode = String(mode || "coordinator");
  if (viewerMode === "coordinator" || viewerMode === "planner") {
    return {
      showFinancePanel: true,
      showBillingContent: true,
      showPaymentsContent: true,
      showBillingAdminTasks: true,
      showMasterdataContent: true,
      showTrainerStatsPanel: false,
    };
  }
  if (viewerMode === "team_captain") {
    return {
      showFinancePanel: false,
      showBillingContent: false,
      showPaymentsContent: false,
      showBillingAdminTasks: false,
      showMasterdataContent: true,
      showTrainerStatsPanel: false,
    };
  }

  if (
    __useIndependentTrainerToolkit({
      mode: viewerMode,
      trainerRow,
      tenantRoles,
    })
  ) {
    return {
      showFinancePanel: true,
      showBillingContent: true,
      showPaymentsContent: true,
      showBillingAdminTasks: true,
      showMasterdataContent: true,
      showTrainerStatsPanel: false,
    };
  }

  const perms = resolveTrainerUiPermissions(trainerRow);
  const implicitSelfBilling = hasSelfManagedBilling && !perms.hasExplicitTabs;
  const showBillingContent = perms.tabs.rechnungswesen || implicitSelfBilling;
  const showPaymentsContent = perms.tabs.zahlungen || implicitSelfBilling;
  const showMasterdataContent = perms.tabs.masterdata;

  const showFinancePanel = showBillingContent || showPaymentsContent;

  return {
    showFinancePanel,
    showBillingContent,
    showPaymentsContent,
    showBillingAdminTasks: perms.tabs.rechnungswesen,
    showMasterdataContent,
    showTrainerStatsPanel: !showFinancePanel,
  };
}

/**
 * Formular → DB-Payload (nur explizite Zusatzrechte; Basis-Reiter implizit).
 * @param {{ billing?: boolean, payments?: boolean, expenses?: boolean, balance?: boolean, administration?: boolean }} flags
 */
export function buildTrainerUiPermissionsPayload({
  billing = false,
  payments = false,
  expenses = false,
  balance = false,
  administration = false,
} = {}) {
  /** @type {Record<string, boolean>} */
  const tabs = {
    hub: true,
    planning: true,
    agenda: true,
    rechnungswesen: !!billing,
    zahlungen: !!payments,
    ausgabenerfassung: !!expenses,
    bilanzierung: !!balance,
    masterdata: !!administration,
  };
  return { tabs };
}

/**
 * @param {unknown} trainerRow
 */
export function readTrainerUiPermissionFlags(trainerRow) {
  const perms = resolveTrainerUiPermissions(trainerRow);
  return {
    billing: perms.tabs.rechnungswesen,
    payments: perms.tabs.zahlungen,
    expenses: perms.tabs.ausgabenerfassung,
    balance: perms.tabs.bilanzierung,
    administration: perms.tabs.masterdata,
  };
}

/**
 * Explizite Billing-Rechte am Trainer-Datensatz (Zugang-Checkboxen).
 * @param {unknown} trainerRow
 */
export function trainerHasExplicitBillingUiPermissions(trainerRow) {
  const perms = resolveTrainerUiPermissions(trainerRow);
  if (!perms.hasExplicitTabs) return false;
  return (
    perms.tabs.rechnungswesen ||
    perms.tabs.zahlungen ||
    perms.tabs.ausgabenerfassung ||
    perms.tabs.bilanzierung
  );
}

/**
 * Billing-Tabs nach Abo-Filter wiederherstellen, wenn der Trainer sie explizit freigegeben hat.
 * Inhalt bleibt ggf. hinter ProFeatureGate (Starter ohne Billing-Feature).
 *
 * @param {Set<string>|Iterable<string>} filteredTabs
 * @param {Set<string>|Iterable<string>} baseTabs
 * @param {unknown} trainerRow
 * @param {{ isSuperAdmin?: boolean }} [opts]
 * @returns {Set<string>}
 */
export function restoreTrainerBillingTabsAfterSubscriptionFilter(
  filteredTabs,
  baseTabs,
  trainerRow,
  opts = {},
) {
  const filtered =
    filteredTabs instanceof Set
      ? filteredTabs
      : new Set(Array.isArray(filteredTabs) ? filteredTabs : [...filteredTabs]);
  if (
    opts.isSuperAdmin ||
    !trainerHasExplicitBillingUiPermissions(trainerRow)
  ) {
    return filtered;
  }
  const base =
    baseTabs instanceof Set
      ? baseTabs
      : new Set(Array.isArray(baseTabs) ? baseTabs : [...baseTabs]);
  const out = new Set(filtered);
  for (const tab of base) {
    if (isSubscriptionBillingTab(tab)) out.add(tab);
  }
  return out;
}
